import { Alignment, LeftOrRightAlignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { ButtonVariant } from "./ButtonVariant";
import { BusyMixinPropDefinitions, BusyMixinProps } from "../../mixins/BusyMixinProps";
import { ComponentPropDefinitions, ComponentPropDefinitionUtil, ComponentProps } from "../../base/ComponentProps";
import { DropdownProps } from "../../page/Overlay";
import { ImagePropDefinitions, ImageProps } from "../image/ImageProps";
import { ListItemType } from "../..";
import { ListProps } from "../list/ListProps";
import { OverlayProps } from "../../page/OverlayProps";
import { PropType } from "../../base/PropType";

export interface ButtonProps extends ComponentProps, BusyMixinProps {
    cancel: boolean;
    caption: string;
    captionWrap: boolean;
    default: boolean;
    dropdownItems: ListItemType[] | (() => ListItemType[]) | (() => Promise<ListItemType[]>);
    dropdownProps: Partial<DropdownProps>;
    dropdownListProps: Partial<ListProps>;
    dropdownOverlayProps: Partial<OverlayProps>;
    focusable: boolean;
    hotkey: string;
    imageAlign: LeftOrRightAlignment;
    imageColor: string;
    imageName: ImageName;
    imageHeight: number;
    imageProps: Partial<ImageProps>,
    imageWidth: number;
    imageRotation: number;
    imageStroke: string;
    imageTransition: string;
    isToggle: boolean;
    link: string;
    requiredLayout: string;
    size: string;
    variant: ButtonVariant;
    wrap: boolean;
}

export class ButtonPropDefinitions {
    private static buttonPropertyDefinitions: ComponentPropDefinitions;

    public static getDefinitions(): ComponentPropDefinitions {
        if (this.buttonPropertyDefinitions == null) {
            this.buttonPropertyDefinitions = {
                ...ComponentPropDefinitionUtil.getComponentPropertyDefinitions(),
                ...ImagePropDefinitions.getContainedImagePropDefs(),
                ...BusyMixinPropDefinitions.getDefinitions(),
                cancel: { type: PropType.bool, visibleInMcLeodTailor: true, description: "This toggles whether or not this button is considered the 'cancel' button.  The cancel button's onClick event will be fired when the user presses the Escape key." },
                caption: { type: PropType.string, visibleInMcLeodTailor: true, description: "This specifies the text that appears in the button." },
                default: { type: PropType.bool, visibleInMcLeodTailor: true, description: "This toggles whether or not this button is considered the 'default' button.  The default button's onClick event will be fired when the user presses the Enter key." },
                focusable: { type: PropType.bool, visibleInMcLeodTailor: true, category: "Image", defaultValue: true, description: "This toggles whether or not this Button can receive focus." },
                hotkey: { type: PropType.string, visibleInMcLeodTailor: true, description: "This specifies a key combination that can be pressed to trigger this buttons onClick event." },
                isToggle: { type: PropType.bool, visibleInMcLeodTailor: true, description: "This specifies whether a button should behave as a toggle button.  When clicked, the button's 'selected' property with change from false to true and the background color will change to toggleBackgroundColor and the text color will change to toggleColor.  This effectively makes the button behave like a checkbox, but with a different UI." },
                imageAlign: {
                    type: PropType.string,
                    dropdownProps: { items: [ Alignment.RIGHT, Alignment.LEFT ] },
                    defaultValue: Alignment.LEFT,
                    visibleInMcLeodTailor: true,
                    description: "This controls where the Button's image (if present) will appear relative to the text."
                },
                link: { type: PropType.string, visibleInMcLeodTailor: true, description: "This specifies a page that will be opened when the button is clicked.  If the Button's dataSource is set, this link can specify a data format string to include data from the dataSource's activeRow." },
                onDropdownSelection: {
                    type: PropType.event,
                    category: "Events",
                    addListenerMethod: "addDropdownSelectionListener",
                    removeListenerMethod: "removeDropdownSelectionListener",
                    eventSignature: "OnDropdownSelection(event: SelectionEvent)",
                    description: "This event will fire when an item from the dropdownItems is selected."
                },
                requiredLayout: { type: PropType.layout, category: "User Access", description: "The layout required for this component. If the user doesn't have access to the layout, the component will not be visible."},
                size: { type: PropType.string, dropdownProps: { items: ["auto", "auto-size-up", "small", "medium", "large"] }, defaultValue: "auto", visibleInMcLeodTailor: true, description: "This specifies the width and font size of the button." },
                toggleBackgroundColor: { type: PropType.color, category: "Appearance", visibleInMcLeodTailor: true, description: "This sets which color fills the background of the button when its isToggle property is set to true and the button is selected." },
                toggleColor: { type: PropType.color, category: "Appearance", visibleInMcLeodTailor: true, description: "This sets which color fills the text color of the button when its isToggle property is set to true and the button is selected." },
                variant: { type: PropType.string, dropdownProps: { items: ["round", "text"] }, visibleInMcLeodTailor: true, description: "This is a shortcut to set several properties at once.  The only allowed values currently are null, 'round', and 'text'  If set to round, no border is painted and the borderRadius is set to 50%.  Usually this is only done if a Button has an image but no text." }
            };
            this.buttonPropertyDefinitions.busy.description = "A button that is busy displays a rotating spinner icon and is disabled.  Being busy indicates that the button's action is in progress.  Some buttons, like Save buttons, automatically handle the busy property as part of their defined action.";
            this.buttonPropertyDefinitions.busyWhenDataSourceBusy.description = "This toggles whether or not the button will automatically be set to busy when its bound datasource is busy.";
            this.buttonPropertyDefinitions.cursor.defaultValue = "pointer";
            ComponentPropDefinitionUtil.populateComponentPropNames(this.buttonPropertyDefinitions);
        }
        return this.buttonPropertyDefinitions;
    }
}
